import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/translations';
import HamburgerIcon from '../HamburgerIcon/HamburgerIcon';
import logoImage from '../../assets/imagemquinta234.png';
import { preloadReservationRequests } from '../../utils/routePreload';
import './Header.css';

function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [scrollDetectionEnabled, setScrollDetectionEnabled] = useState(true);
    const [suppressScrollDetection, setSuppressScrollDetection] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const headerRef = useRef(null);
    const expectUserTouchRef = useRef(false);
    const { language, changeLanguage } = useLanguage();
    const t = translations?.[language] || translations?.pt || {};

    useEffect(() => {
        // Listen for custom event to disable scroll detection
        const handleDisableScrollDetection = () => {
            setScrollDetectionEnabled(false);
            setIsScrolled(false);
            // Hold off on re-enabling until the next real user interaction
            expectUserTouchRef.current = true;
            if (headerRef.current) headerRef.current.classList.remove('scrolled');

            // Suppress scroll events briefly to ignore programmatic scroll from navigation
            setSuppressScrollDetection(true);
        };

        window.addEventListener('disableHeaderScrollDetection', handleDisableScrollDetection);

        return () => {
            window.removeEventListener('disableHeaderScrollDetection', handleDisableScrollDetection);
        };
    }, []);

    useEffect(() => {
        let ticking = false;
        let touchEndTimer = null;
        let topCheckRafId = null;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    // Only update scroll state if detection is enabled
                    if (scrollDetectionEnabled) {
                        updateScrollState(true);
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        const updateScrollState = (forceTopReset = false) => {
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            const onReservations = (location && location.pathname === '/reservations');
            const scrolledNow = onReservations || scrollY > 50;
            const shouldResetToTop = forceTopReset && !onReservations && scrollY <= 15;
            const nextState = shouldResetToTop ? false : scrolledNow;
            setIsScrolled(nextState);
            if (headerRef.current) headerRef.current.classList.toggle('scrolled', nextState);
        };

        const handleTouchMove = () => {
            // Clear suppression flags on first user touch
            if (suppressScrollDetection) {
                setSuppressScrollDetection(false);
            }
            if (expectUserTouchRef.current) {
                expectUserTouchRef.current = false;
            }
            // User is actively touching; re-enable detection and update immediately
            setScrollDetectionEnabled(true);

            // Force immediate scroll state update, and ensure top snap hides the bar instantly
            updateScrollState(true);
        };

        const handleTouchStart = () => {
            // Clear suppression flags on first touch and snap state in case we're already at top
            if (suppressScrollDetection) {
                setSuppressScrollDetection(false);
            }
            if (expectUserTouchRef.current) {
                expectUserTouchRef.current = false;
            }
            setScrollDetectionEnabled(true);
            updateScrollState(true);

            // Start continuous RAF check while user is touching to catch top position instantly
            const checkTop = () => {
                const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
                if (scrollY <= 15) {
                    setIsScrolled(false);
                    if (headerRef.current) headerRef.current.classList.remove('scrolled');
                }
                topCheckRafId = window.requestAnimationFrame(checkTop);
            };
            topCheckRafId = window.requestAnimationFrame(checkTop);
        };

        const handleTouchEnd = () => {
            // Continue a short RAF window after touch end to catch momentum scroll finishing at the very top
            if (topCheckRafId) {
                window.cancelAnimationFrame(topCheckRafId);
                topCheckRafId = null;
            }
            const endTime = performance.now() + 450;
            const postTouchCheck = () => {
                const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
                if (scrollY <= 15) {
                    setIsScrolled(false);
                    if (headerRef.current) headerRef.current.classList.remove('scrolled');
                }
                if (performance.now() < endTime) {
                    topCheckRafId = window.requestAnimationFrame(postTouchCheck);
                } else {
                    topCheckRafId = null;
                }
            };
            topCheckRafId = window.requestAnimationFrame(postTouchCheck);

            // Clear suppression on user touch
            if (suppressScrollDetection) {
                setSuppressScrollDetection(false);
            }
            // If we are suppressing (e.g., after menu click), ignore tap touchend
            if (expectUserTouchRef.current) return;

            // Re-enable scroll detection on touch end (user is actively scrolling)
            setScrollDetectionEnabled(true);

            // Clear any existing timer
            if (touchEndTimer) clearTimeout(touchEndTimer);

            // Force immediate scroll state update with top reset safety
            updateScrollState(true);

            // Check again after momentum scroll might have finished (iOS Safari)
            touchEndTimer = setTimeout(() => {
                updateScrollState(true);
                // One more check for iOS elastic scroll
                setTimeout(() => updateScrollState(true), 100);
            }, 50);
        };

        const handleUserScroll = () => {
            // Clear suppression on first user scroll
            if (suppressScrollDetection) {
                setSuppressScrollDetection(false);
            }

            // Clear expectUserTouchRef and re-enable detection
            if (expectUserTouchRef.current) {
                expectUserTouchRef.current = false;
                setScrollDetectionEnabled(true);
            }

            // Force immediate scroll state update and snap off at top
            updateScrollState(true);
        };

        // Add listeners including touchmove and touchend for better mobile support
        window.addEventListener('scroll', handleUserScroll, { passive: true });
        window.addEventListener('wheel', handleUserScroll, { passive: true });
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('touchend', handleTouchEnd, { passive: true });

        // Run initial checks only if detection is enabled
        let timeoutId;
        if (scrollDetectionEnabled) {
            handleScroll();
            timeoutId = setTimeout(() => updateScrollState(true), 100);
        }

        return () => {
            window.removeEventListener('scroll', handleUserScroll);
            window.removeEventListener('wheel', handleUserScroll);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            if (timeoutId) clearTimeout(timeoutId);
            if (touchEndTimer) clearTimeout(touchEndTimer);
            if (topCheckRafId) window.cancelAnimationFrame(topCheckRafId);
        };
    }, [location, scrollDetectionEnabled, suppressScrollDetection]);
    const handleMenuClick = () => {
        // Add your menu toggle logic here
        console.log('Menu clicked');
    };

    const handleReservationClick = () => {
        navigate('/reservations');
    };

    const handleLogoClick = () => {
        const path = location.pathname || '';
        // If on a villa detail or reservations page, perform a full reload to
        // ensure the main page is loaded and the user lands at the top.
        if (path.startsWith('/villa/') || path === '/reservations') {
            window.location.assign('/');
            return;
        }

        // If already on the main page, reload to guarantee top position.
        if (path === '/') {
            window.location.assign('/');
            return;
        }

        // Otherwise navigate via router to the main page.
        navigate('/');
    };

    return (
        <header ref={headerRef} className={`header ${isScrolled ? 'scrolled' : ''}`}>
            <div className="white-banner"></div>
            <div className="navigation">
                <HamburgerIcon onClick={handleMenuClick} />
                <button
                    className="menu-cta"
                    onClick={handleReservationClick}
                    onMouseEnter={preloadReservationRequests}
                    onFocus={preloadReservationRequests}
                    aria-label="Book now"
                >
                    {t.bookNow}
                </button>
                <div className="language-selector">
                    <button
                        className={`lang-btn ${language === 'pt' ? 'active' : ''}`}
                        onClick={() => changeLanguage('pt')}
                        aria-label="Portuguese"
                    >PT</button>
                    <span className="lang-divider">|</span>
                    <button
                        className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                        onClick={() => changeLanguage('en')}
                        aria-label="English"
                    >EN</button>
                </div>
                <div
                    className="header-logo"
                    onClick={handleLogoClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleLogoClick();
                        }
                    }}
                    aria-label="Scroll to top"
                >
                    <img src={logoImage} alt="Tapada de São Domingos Logo" onClick={handleLogoClick} />
                </div>



                {/* <NavLinks />  */}
                {/* <NavSocials /> */}
            </div>
        </header>
    );
}

export default Header;